import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { PWAProvider } from "./context/PWAContext";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Kamar from "./pages/Kamar";
import Penghuni from "./pages/Penghuni";
import DetailPenghuni from "./pages/DetailPenghuni";
import Pembayaran from "./pages/Pembayaran";
import Laporan from "./pages/Laporan";
import AuditLog from "./pages/AuditLog";
import KamarSaya from "./pages/KamarSaya";
import BiayaOperasional from "./pages/BiayaOperasional";
import Fasilitas from "./pages/Fasilitas";
import Pengaduan from "./pages/Pengaduan";
import Staff from "./pages/Staff";
import RekeningBank from "./pages/RekeningBank";
import Profile from "./pages/Profile";
import Admin from "./pages/Admin";
import Layout from "./components/Layout";

// Protected Route Component
export const ProtectedRoute = ({ children, requirePemilik = false, requireOperator = false, requirePenghuni = false }) => {
  const { isAuthenticated, loading, isPemilik, canManageKos, isPenghuni } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 spinner"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if ((requirePemilik && !isPemilik()) ||
      (requireOperator && !canManageKos()) ||
      (requirePenghuni && !isPenghuni())) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public Route Component (redirect if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 spinner"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

export function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />

      {/* Protected Routes - All Users */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/pembayaran" element={<ProtectedRoute><Pembayaran /></ProtectedRoute>} />
      <Route path="/pengaduan" element={<ProtectedRoute><Pengaduan /></ProtectedRoute>} />

      {/* Protected Routes - Pemilik and Admin (operational access) */}
      <Route path="/kamar" element={<ProtectedRoute requireOperator><Kamar /></ProtectedRoute>} />
      <Route path="/penghuni" element={<ProtectedRoute requireOperator><Penghuni /></ProtectedRoute>} />
      <Route path="/penghuni/:id" element={<ProtectedRoute requireOperator><DetailPenghuni /></ProtectedRoute>} />
      <Route path="/laporan" element={<ProtectedRoute requireOperator><Laporan /></ProtectedRoute>} />
      <Route path="/audit-log" element={<ProtectedRoute requireOperator><AuditLog /></ProtectedRoute>} />
      <Route path="/biaya-operasional" element={<ProtectedRoute requireOperator><BiayaOperasional /></ProtectedRoute>} />
      <Route path="/fasilitas" element={<ProtectedRoute requireOperator><Fasilitas /></ProtectedRoute>} />
      <Route path="/staff" element={<ProtectedRoute requireOperator><Staff /></ProtectedRoute>} />
      {/* Only the owner manages bank accounts and Admin access. */}
      <Route path="/rekening-bank" element={<ProtectedRoute requirePemilik><RekeningBank /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute requirePemilik><Admin /></ProtectedRoute>} />

      {/* Protected Routes - Penghuni Only */}
      <Route path="/kamar-saya" element={<ProtectedRoute requirePenghuni><KamarSaya /></ProtectedRoute>} />

      {/* Protected Routes - Profile (All Users) */}
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />

      {/* Redirect */}
      <Route path="/" element={<Landing />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

// Import InstallPWA component
import InstallPWA from "./components/InstallPWA";

function App() {
  return (
    <BrowserRouter>
      <PWAProvider>
        <AuthProvider>
          <AppRoutes />
          <InstallPWA />
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </PWAProvider>
    </BrowserRouter>
  );
}

export default App;
